/**
 * SDK 客服配置解析(同源 BFF 调用)
 *
 * 客服配置来自子系统接口(需 X-API-Key,SDK 浏览器侧无密钥),故走 hotel-hub 同源 BFF
 * /api/tenant/subsystems(BFF 持密钥服务端代理)。用 raw fetch 同源 BFF（独立于 sdk/api/client.ts，
 * 直接 resolveBffDomain 透传 domain；sdk/api/client.ts 第 4 步起亦走同源 BFF，但本模块历史独立实现）。
 *
 * 返回 { tenantId, chatUrl } | null。null=未开通/未配置/异常,调用方不挂载(fail-closed)。
 * 页面级内存缓存(按 domain):仅当前页面生命周期内复用,刷新即失效--与主流程 SSR
 * 每次全量刷新对齐(后台改客服配置后,下次加载即可见)。历史用 sessionStorage+5min TTL,
 * 跨刷新存活致后台关闭入口后 SDK 仍展示旧配置最多 5 分钟,已弃用。
 *
 * domain 透传:dev 用 ?domain= 模拟时需把 domain 带给 BFF(proxy 据此注入 x-hotel-domain);
 * 生产走子域名,BFF 用 host 头解析,无需透传。逻辑与 hotel-hub client.ts 的 BFF 转发一致。
 */
import type { CustomerServiceConfig } from '@/types/hotel';
import { getLastDomain } from '@/lib/shared-storage';
import { getConfig } from '../config';

const REQUEST_TIMEOUT = 15000;
const BFF_PATH = '/api/tenant/subsystems';

/**
 * 页面级内存缓存(按 domain)。
 *
 * 仅当前页面生命周期内复用(并发去重 + 避免同页多次打 BFF),刷新即失效,下次加载重新拉取,
 * 与 hotel-hub SSR 行为对齐。失败不缓存(fail-closed,瞬时故障下次重试)。
 */
const csCache = new Map<string, CustomerServiceConfig | null>();

/** 当前页面 domain(dev 透传 ?domain= 给 BFF;生产靠 host 头) */
function resolveBffDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const queryDomain = new URLSearchParams(window.location.search).get('domain');
  if (queryDomain) return queryDomain;
  return getLastDomain() || undefined;
}

function readCache(domain: string | undefined): CustomerServiceConfig | null | undefined {
  return csCache.get(domain || '_');
}

function writeCache(domain: string | undefined, data: CustomerServiceConfig | null): void {
  csCache.set(domain || '_', data);
}

/** 并发去重锁(同一次页面加载内多实例/多请求共享同一次 fetch) */
let csFetching: Promise<CustomerServiceConfig | null> | null = null;

/**
 * 解析当前租户的客服配置(缓存 + 并发去重)。
 * fail-closed:任何异常 -> null;仅 HTTP 200 才缓存(含 null=未开通/未配置),瞬时失败不缓存。
 */
export async function fetchCustomerServiceConfig(): Promise<CustomerServiceConfig | null> {
  const domain = resolveBffDomain();
  const cached = readCache(domain);
  if (cached !== undefined) return cached;

  if (!csFetching) {
    csFetching = (async () => {
      try {
        const { basePath } = getConfig();
        const url = `${basePath}${BFF_PATH}${
          domain ? `?domain=${encodeURIComponent(domain)}` : ''
        }`;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
        let res: Response;
        try {
          res = await fetch(url, {
            method: 'GET',
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          });
        } finally {
          clearTimeout(timer);
        }
        if (!res.ok) return null; // 非 200:fail-closed,不缓存
        const data = await res.json();
        const result =
          data &&
          typeof data.tenantId === 'string' &&
          typeof data.chatUrl === 'string' &&
          data.tenantId &&
          data.chatUrl
            ? (data as CustomerServiceConfig)
            : null;
        writeCache(domain, result); // 含 null(未开通)也缓存
        return result;
      } catch {
        return null; // 网络/解析异常:fail-closed,不缓存
      } finally {
        csFetching = null;
      }
    })();
  }
  return csFetching;
}
